import React, { useEffect, useState, useCallback } from 'react'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api, apiRequest } from '../../../lib/api'

const MAX_ACTIVE_FIELDS = 3
const CAP_MESSAGE =
  'A parish may have at most 3 active donation fields. Archive one to add another.'

const OPTION_VALUE_PATTERN = /^[a-zA-Z0-9]+$/

// Stripe requires custom_fields[].dropdown.options[].value to be "unique to
// this option, alphanumeric, and up to 100 characters" — see
// https://docs.stripe.com/api/checkout/sessions/create (custom_fields). A
// hyphenated slug makes Stripe reject the whole Checkout Session, which blocks
// every donation to the parish. So the derived value is camelCase with the
// separators removed rather than hyphenated: still readable, still distinct
// between "Building Fund" and "Youth Ministry", and legal on the wire.
//
// Accented letters are folded to ASCII (NFD + combining-mark strip) so
// "Edificación" yields "edificacion" rather than losing the letter entirely.
function slugify(label) {
  const words = (label || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
  if (words.length === 0) return ''
  return words
    .map((word, i) =>
      i === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join('')
    .slice(0, 100)
}

function emptyOption() {
  return { label: '', value: '', valueEdited: false }
}

function blankForm() {
  return {
    label: '',
    type: 'TEXT',
    required: false,
    options: [],
  }
}

function fieldToForm(field) {
  return {
    label: field.label || '',
    type: field.type,
    required: !!field.required,
    options: (field.options || []).map((o) => ({
      label: o.label,
      value: o.value,
      valueEdited: true,
    })),
  }
}

export default function DonationFields({ parishId: parishIdProp }) {
  const { getParishAdminParishId } = useUserRole()

  const [parishId, setParishId] = useState(parishIdProp || null)
  const [fields, setFields] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(blankForm())
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const resolveParishId = async () => {
    if (parishIdProp) return parishIdProp
    let id = getParishAdminParishId()
    if (!id) {
      const userData = await apiRequest('/api/v1/auth/me')
      const roleDetails = userData?.roleDetails || []
      id = roleDetails.find((r) => r.name === 'parish-admin')?.parishId
    }
    return id
  }

  const loadFields = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const id = await resolveParishId()
      if (!id) {
        setError('You are not associated with a parish as a parish admin.')
        setLoading(false)
        return
      }
      setParishId(id)
      const result = await api.getParishDonationFields(id, { includeArchived: true })
      setFields(result || [])
    } catch (err) {
      setError(err.message || 'Failed to load donation fields')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parishIdProp])

  useEffect(() => {
    loadFields()
  }, [loadFields])

  const activeFields = fields
    .filter((f) => !f.archivedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder)
  const archivedFields = fields.filter((f) => f.archivedAt)

  const atCap = activeFields.length >= MAX_ACTIVE_FIELDS

  // ─── reordering ───────────────────────────────────────────────────────────
  const moveField = async (index, direction) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= activeFields.length) return
    const reordered = [...activeFields]
    ;[reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]]
    const orderedIds = reordered.map((f) => f.id)
    try {
      await api.reorderParishDonationFields(parishId, orderedIds)
      await loadFields()
    } catch (err) {
      setError(err.message || 'Failed to reorder fields')
    }
  }

  // ─── add / edit modal ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null)
    setForm(blankForm())
    setModalError('')
    setModalOpen(true)
  }

  const openEditModal = (field) => {
    setEditingId(field.id)
    setForm(fieldToForm(field))
    setModalError('')
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setModalError('')
  }

  const updateOption = (index, patch) => {
    setForm((f) => {
      const options = f.options.map((o, i) => (i === index ? { ...o, ...patch } : o))
      return { ...f, options }
    })
  }

  const handleOptionLabelChange = (index, label) => {
    setForm((f) => {
      const current = f.options[index]
      const nextValue = current.valueEdited ? current.value : slugify(label)
      const options = f.options.map((o, i) =>
        i === index ? { ...o, label, value: nextValue } : o,
      )
      return { ...f, options }
    })
  }

  const handleOptionValueChange = (index, value) => {
    updateOption(index, { value, valueEdited: true })
  }

  const addOption = () => {
    setForm((f) => ({ ...f, options: [...f.options, emptyOption()] }))
  }

  const removeOption = (index) => {
    setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== index) }))
  }

  const validateForm = () => {
    if (!form.label.trim()) return 'Label is required.'
    if (form.label.length > 50) return 'Label must be 50 characters or fewer.'
    if (form.type === 'SINGLE_SELECT') {
      if (form.options.length < 1) return 'Add at least one option for a dropdown field.'
      if (form.options.length > 200) return 'A dropdown field can have at most 200 options.'
      const values = new Set()
      for (const opt of form.options) {
        if (!opt.label.trim() || !opt.value.trim()) {
          return 'Every option needs both a label and a value.'
        }
        if (opt.label.length > 100 || opt.value.length > 100) {
          return 'Option labels and values must be 100 characters or fewer.'
        }
        if (!OPTION_VALUE_PATTERN.test(opt.value)) {
          return `Option value "${opt.value}" must use only letters and numbers (no spaces, hyphens, underscores or accents) — Stripe rejects anything else. The label can contain anything.`
        }
        if (values.has(opt.value)) {
          return `Option value "${opt.value}" is used more than once.`
        }
        values.add(opt.value)
      }
    }
    return null
  }

  const handleSave = async (e) => {
    e.preventDefault()
    const validationError = validateForm()
    if (validationError) {
      setModalError(validationError)
      return
    }
    setSaving(true)
    setModalError('')
    const body = {
      label: form.label.trim(),
      type: form.type,
      required: form.required,
      options:
        form.type === 'SINGLE_SELECT'
          ? form.options.map((o) => ({ label: o.label.trim(), value: o.value.trim() }))
          : [],
    }
    try {
      if (editingId) {
        await api.updateParishDonationField(parishId, editingId, body)
      } else {
        await api.createParishDonationField(parishId, body)
      }
      setModalOpen(false)
      await loadFields()
    } catch (err) {
      setModalError(err.message || 'Failed to save field')
    } finally {
      setSaving(false)
    }
  }

  // ─── archive / restore ────────────────────────────────────────────────────
  const handleArchive = async (field) => {
    const confirmed = window.confirm(
      `Archive "${field.label}"? Past donations keep their answers to this field — only new donations will stop collecting it. You can restore it later.`,
    )
    if (!confirmed) return
    try {
      await api.archiveParishDonationField(parishId, field.id)
      await loadFields()
    } catch (err) {
      setError(err.message || 'Failed to archive field')
    }
  }

  const handleRestore = async (field) => {
    try {
      await api.restoreParishDonationField(parishId, field.id)
      await loadFields()
    } catch (err) {
      setError(err.message || 'Failed to restore field')
    }
  }

  if (loading) {
    return <div className="p-8 text-charcoal-muted">Loading...</div>
  }

  return (
    <div className="p-8 max-w-4xl mx-auto space-y-10">
      <div>
        <h1 className="text-2xl font-semibold mb-1">Donation fields</h1>
        <p className="text-charcoal-muted">
          Collect extra information from donors when they give, such as a memorial dedication or
          which fund a gift should support. Donors see these fields on the giving form and their
          answers show up on your donation reports and receipts.
        </p>
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">Active fields ({activeFields.length} of {MAX_ACTIVE_FIELDS})</h2>
          <Button
            type="button"
            onClick={openAddModal}
            disabled={atCap}
            title={atCap ? CAP_MESSAGE : undefined}
            className="bg-gold text-black border-gold hover:bg-gold/90"
          >
            Add field
          </Button>
        </div>
        {atCap && <p className="text-charcoal-muted text-sm mb-4">{CAP_MESSAGE}</p>}

        {activeFields.length === 0 ? (
          <p className="text-charcoal-muted text-sm">No donation fields yet.</p>
        ) : (
          <ul className="space-y-3">
            {activeFields.map((field, index) => (
              <li
                key={field.id}
                className="border rounded-md p-4 flex items-start justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{field.label}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-charcoal-muted bg-surface-linen border border-surface-border rounded px-1.5 py-0.5">
                      {field.type === 'SINGLE_SELECT' ? 'Dropdown' : 'Text'}
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-charcoal-muted bg-surface-linen border border-surface-border rounded px-1.5 py-0.5">
                      {field.required ? 'Required' : 'Optional'}
                    </span>
                  </div>
                  {field.type === 'SINGLE_SELECT' && field.options?.length > 0 && (
                    <p className="text-charcoal-muted text-sm mt-1">
                      Options: {field.options.map((o) => o.label).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={`Move ${field.label} up`}
                    disabled={index === 0}
                    onClick={() => moveField(index, -1)}
                  >
                    ▲
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={`Move ${field.label} down`}
                    disabled={index === activeFields.length - 1}
                    onClick={() => moveField(index, 1)}
                  >
                    ▼
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => openEditModal(field)}>
                    Edit
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => handleArchive(field)}>
                    Archive
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <button
          type="button"
          onClick={() => setShowArchived((s) => !s)}
          className="text-sm font-medium text-charcoal-muted hover:text-charcoal"
        >
          {showArchived ? 'Hide' : 'Show'} inactive fields ({archivedFields.length})
        </button>
        {showArchived && (
          <div className="mt-4">
            {atCap && (
              <p className="text-charcoal-muted text-sm mb-3">
                Restoring is disabled while 3 fields are active. Archive an active field first.
              </p>
            )}
            {archivedFields.length === 0 ? (
              <p className="text-charcoal-muted text-sm">No archived fields.</p>
            ) : (
              <ul className="space-y-3">
                {archivedFields.map((field) => (
                  <li
                    key={field.id}
                    className="border rounded-md p-4 flex items-start justify-between gap-4 opacity-70"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{field.label}</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-charcoal-muted bg-surface-linen border border-surface-border rounded px-1.5 py-0.5">
                          {field.type === 'SINGLE_SELECT' ? 'Dropdown' : 'Text'}
                        </span>
                      </div>
                      <p className="text-charcoal-muted text-sm mt-1">
                        Not shown to donors. Past donations keep their answers.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={atCap}
                      title={atCap ? CAP_MESSAGE : undefined}
                      onClick={() => handleRestore(field)}
                    >
                      Restore
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="donation-field-modal-title"
            className="bg-white rounded-md max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <h3 id="donation-field-modal-title" className="text-lg font-medium">
              {editingId ? 'Edit field' : 'Add field'}
            </h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label htmlFor="field-label" className="mb-2 block">Label</Label>
                <Input
                  id="field-label"
                  required
                  maxLength={50}
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. In memory of"
                />
              </div>

              <div>
                <Label htmlFor="field-type" className="mb-2 block">Type</Label>
                <select
                  id="field-type"
                  className="border rounded-md px-3 py-2 w-full"
                  value={form.type}
                  disabled={!!editingId}
                  title={editingId ? "A field's type can't change once it has been used. Archive it and create a new one instead." : undefined}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                >
                  <option value="TEXT">Text</option>
                  <option value="SINGLE_SELECT">Dropdown</option>
                </select>
                {editingId && (
                  <p className="text-charcoal-muted text-xs mt-1">
                    A field's type can't be changed once donations may have used it. Archive this
                    field and create a new one if you need a different type.
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="field-required"
                  type="checkbox"
                  checked={form.required}
                  onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))}
                />
                <Label htmlFor="field-required" className="normal-case tracking-normal font-medium text-charcoal">
                  Required
                </Label>
              </div>
              <p className="text-charcoal-muted text-xs -mt-3">
                Donors must answer this field before completing a gift.
              </p>

              {form.type === 'SINGLE_SELECT' && (
                <div>
                  <Label className="mb-2 block">Options</Label>
                  <p className="text-charcoal-muted text-xs mb-2">
                    The value is what's stored on every donation, so avoid changing it once
                    donors have started choosing this option — it's filled in from the label,
                    but you can set it yourself. Values must be letters and numbers only
                    (no spaces, hyphens or underscores); labels can contain anything.
                  </p>
                  <div className="space-y-2">
                    {form.options.map((opt, index) => (
                      <div key={index} className="flex items-end gap-2">
                        <div className="flex-1">
                          <Label htmlFor={`option-label-${index}`} className="mb-1 block">Label</Label>
                          <Input
                            id={`option-label-${index}`}
                            maxLength={100}
                            value={opt.label}
                            onChange={(e) => handleOptionLabelChange(index, e.target.value)}
                            placeholder="e.g. Building Fund"
                          />
                        </div>
                        <div className="flex-1">
                          <Label htmlFor={`option-value-${index}`} className="mb-1 block">Value</Label>
                          <Input
                            id={`option-value-${index}`}
                            maxLength={100}
                            value={opt.value}
                            onChange={(e) => handleOptionValueChange(index, e.target.value)}
                            placeholder="e.g. buildingFund"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          aria-label="Remove option"
                          onClick={() => removeOption(index)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addOption}>
                    Add option
                  </Button>
                </div>
              )}

              {modalError && <div className="text-red-600 text-sm">{modalError}</div>}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeModal} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-gold text-black border-gold hover:bg-gold/90"
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
